import { create } from "zustand";
import { trendingMoviesService } from "@/services/trendingMoviesService";
import { showToast } from "@/lib/toast";
import {
  TrendingMoviesWithDetails,
  UpdateTrendingMoviesDto,
  TrendingType,
} from "@/types/trending-movies";

interface TrendingMoviesState {
  // Trending movies data
  trendingMovies: TrendingMoviesWithDetails | null;
  currentType: TrendingType;
  isLoading: boolean;
  isUpdating: boolean;
  error: string | null;

  // Actions
  fetchTrendingMovies: (type?: TrendingType) => Promise<void>;
  updateTrendingMovies: (updateDto: UpdateTrendingMoviesDto) => Promise<void>;
  setCurrentType: (type: TrendingType) => void;
  clearError: () => void;
  resetTrendingMovies: () => void;
}

export const useTrendingMoviesStore = create<TrendingMoviesState>((set, get) => ({
  // Initial state
  trendingMovies: null,
  currentType: TrendingType.TRENDING,
  isLoading: false,
  isUpdating: false,
  error: null,

  // Actions
  fetchTrendingMovies: async (type?: TrendingType) => {
    const targetType = type ?? get().currentType;
    set({ isLoading: true, error: null, currentType: targetType });
    try {
      const trendingMovies: any =
        await trendingMoviesService.getTrendingMovies(targetType);
      set({ trendingMovies: trendingMovies?.data, isLoading: false });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Lỗi khi tải danh sách phim";
      set({
        error: errorMessage,
        isLoading: false,
      });
      showToast.error("Lỗi", errorMessage);
    }
  },

  updateTrendingMovies: async (updateDto: UpdateTrendingMoviesDto) => {
    set({ isUpdating: true, error: null });
    try {
      const updatedTrendingMovies: any =
        await trendingMoviesService.updateTrendingMovies(updateDto);
      set({ trendingMovies: updatedTrendingMovies?.data, isUpdating: false });
      showToast.success("Thành công", "Cập nhật danh sách phim thành công");
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Lỗi khi cập nhật danh sách phim";
      set({
        error: errorMessage,
        isUpdating: false,
      });
      showToast.error("Lỗi", errorMessage);
      throw error;
    }
  },

  setCurrentType: (type: TrendingType) => set({ currentType: type }),

  clearError: () => set({ error: null }),
  resetTrendingMovies: () =>
    set({
      trendingMovies: null,
      error: null,
    }),
}));
